import React, { useCallback } from 'react';

import { useForm } from 'react-hook-form';

import { Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { categories as propertyCategories } from '@luxuryescapes/lib-global';

import { Autocomplete, Input, Select } from '~/components/Common/Forms/fields';

import { SUPPLIER_NAMES } from '~/consts/bedbank';
import { booleanOptions, countriesIsoOptions, ratingOperations, ratingValues } from '~/consts/bedbankFilterOptions';

import { toTitleCase } from '~/utils/toTitleCase';

import { FilterQuery } from './type';

type Option = {
  label: string;
  value: string;
};

interface Props {
  queries: FilterQuery;
  clearSearch: () => void;
  handleSubmit: (query?: FilterQuery) => void;
}

function SearchForm(props: Props) {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      filterByName: props.queries.filterByName,
      filterBySupplier: props.queries.filterBySupplier,
      filterBySupplierPropertyId: props.queries.filterBySupplierPropertyId,
      filterByMasterPropertyId: props.queries.filterByMasterPropertyId,
      filterByRatingOp: props.queries.filterByRatingOp,
      filterByRatingVal: props.queries.filterByRatingVal,
      filterByGuestRatingCountOp: props.queries.filterByGuestRatingCountOp,
      filterByGuestRatingCountVal: props.queries.filterByGuestRatingCountVal,
      filterByGuestRatingOverallOp: props.queries.filterByGuestRatingOverallOp,
      filterByGuestRatingOverallVal: props.queries.filterByGuestRatingOverallVal,
      filterByRankOp: props.queries.filterByRankOp,
      filterByRankVal: props.queries.filterByRankVal,
      filterByMapped: props.queries.filterByMapped,
      filterByMappedIsDeleted: props.queries.filterByMappedIsDeleted,
      filterByHasImages: props.queries.filterByHasImages,
      filterByUpdateBlocked: props.queries.filterByUpdateBlocked,
      filterByHasRooms: props.queries.filterByHasRooms,
      filterByMultiUnit: props.queries.filterByMultiUnit,
      filterByCategories: props.queries.filterByCategories,
      filterByCountryCode: (props.queries.filterByCountryCode ?? [])
        .map((x) => countriesIsoOptions.find((n) => n.value === x))
        .filter(Boolean),
    },
    mode: 'onBlur',
  });

  const clearFilters = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      props.clearSearch();
    },
    [props.clearSearch],
  );

  return (
    <Stack direction="column" spacing={2}>
      <Box>
        <Typography variant="h6">Filter By</Typography>
      </Box>
      <Box>
        <form
          onSubmit={handleSubmit((q) =>
            props.handleSubmit({
              filterByName: q.filterByName,
              filterBySupplier: q.filterBySupplier,
              filterBySupplierPropertyId: q.filterBySupplierPropertyId,
              filterByMasterPropertyId: q.filterByMasterPropertyId,
              filterByRatingOp: q.filterByRatingOp,
              filterByRatingVal: q.filterByRatingVal,
              filterByGuestRatingCountOp: q.filterByGuestRatingCountOp,
              filterByGuestRatingCountVal: q.filterByGuestRatingCountVal,
              filterByGuestRatingOverallOp: q.filterByGuestRatingOverallOp,
              filterByGuestRatingOverallVal: q.filterByGuestRatingOverallVal,
              filterByRankOp: q.filterByRankOp,
              filterByRankVal: q.filterByRankVal,
              filterByMapped: q.filterByMapped ? q.filterByMapped : undefined,
              filterByMappedIsDeleted: q.filterByMapped ? q.filterByMappedIsDeleted : undefined,
              filterByHasImages: q.filterByHasImages,
              filterByUpdateBlocked: q.filterByUpdateBlocked,
              filterByHasRooms: q.filterByHasRooms,
              filterByMultiUnit: q.filterByMultiUnit,
              filterByCategories: q.filterByCategories,
              filterByCountryCode: q.filterByCountryCode?.map((x: Option) => x.value),
            }),
          )}
        >
          <Grid spacing={2} container mb={2}>
            <Grid size={{ md: 4 }}>
              <Input fullWidth control={control} muiLabel="Enter Hotel Name" name="filterByName" />
            </Grid>
            <Grid size={{ md: 4 }}>
              <Input
                fullWidth
                control={control}
                muiLabel="Enter Supplier Property Id"
                name="filterBySupplierPropertyId"
              />
            </Grid>
            <Grid size={{ md: 4 }}>
              <Input fullWidth control={control} muiLabel="Enter Master Property Id" name="filterByMasterPropertyId" />
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterBySupplier" muiLabel="Supplier">
                {['all', ...SUPPLIER_NAMES].map((supplier) => (
                  <MenuItem key={supplier} value={supplier === 'all' ? '' : supplier}>
                    {toTitleCase(supplier)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Autocomplete
                multiple
                control={control}
                getOptionLabel={(option: Option) => option.label}
                name="filterByCountryCode"
                muiLabel="Countries"
                options={countriesIsoOptions}
              />
            </Grid>
            <Grid size={{ md: 2 }}>
              <Autocomplete
                multiple
                control={control}
                getOptionLabel={(option: string) => option.replace(/_/g, ' ')}
                name="filterByCategories"
                muiLabel="Categories"
                options={propertyCategories.allCategories}
              />
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterByMultiUnit" muiLabel="Multi unit?">
                {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterByMapped" muiLabel="Has mapped?">
                {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            {props.queries?.filterByMapped && (
              <Grid size={{ md: 2 }}>
                <Select control={control} name="filterByMappedIsDeleted" muiLabel="Is mapped deleted?">
                  {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            )}
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterByHasRooms" muiLabel="Has rooms?">
                {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterByHasImages" muiLabel="Has images?">
                {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterByUpdateBlocked" muiLabel="Is Update Blocked?">
                {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Stack spacing={2}>
            <Grid spacing={2} container mb={2}>
              <Grid size={{ md: 4 }}>
                <Stack direction="row" spacing={2}>
                  <Select control={control} name="filterByRatingOp" muiLabel="Star Rating operator">
                    {ratingOperations.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select control={control} name="filterByRatingVal" muiLabel="Star Rating value">
                    {ratingValues.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Grid>
              <Grid size={{ md: 4 }}>
                <Stack direction="row" spacing={2}>
                  <Select control={control} name="filterByGuestRatingCountOp" muiLabel="Guest rating count operator">
                    {ratingOperations.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Input
                    fullWidth
                    control={control}
                    muiLabel="Guest rating count value"
                    name="filterByGuestRatingCountVal"
                  />
                </Stack>
              </Grid>
              <Grid size={{ md: 4 }}>
                <Stack direction="row" spacing={2}>
                  <Select
                    control={control}
                    name="filterByGuestRatingOverallOp"
                    muiLabel="Guest rating overall operator"
                  >
                    {ratingOperations.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Input
                    fullWidth
                    control={control}
                    muiLabel="Guest rating overall value"
                    name="filterByGuestRatingOverallVal"
                  />
                </Stack>
              </Grid>
              <Grid size={{ md: 4 }}>
                <Stack direction="row" spacing={2}>
                  <Select control={control} name="filterByRankOp" muiLabel="Rank operator">
                    {ratingOperations.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Input fullWidth control={control} muiLabel="Rank value" name="filterByRankVal" />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center" padding={5}>
            <Box>
              <Button type="button" variant="contained" onClick={clearFilters}>
                Clear
              </Button>
            </Box>
            <Box>
              <Button type="submit" variant="contained">
                Filter
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </Stack>
  );
}

export default SearchForm;
